import { useContext, useEffect, useState } from "react";
import { SelectedServiceContext } from "@context/ISelectedContext";
import { MOBILE_DOMAIN } from "@constants";
import { ESubscriptionStatus, ICustomService, IServicePage, ISubscriptionPage } from "../interface/ISubscription";
import { getSimReplacePendingRoute, GET_SERVICE_LIST_ROUTE, GET_SUBSCRIPTION_LIST_ROUTE } from "@api-routes";
import { useFetch } from "@common/hook/useFetch";
import { ISelectOption } from "src/itsf/interfaces/ISelectOption";

type CustomerServiceOption = ISelectOption<ICustomService>;

export const useGetSubscription = (hasDefault: boolean, setShowNoServiceChildren?: (val: boolean) => void) => {
    const { selectedService, setSelectedService } = useContext(SelectedServiceContext);
    const [servicesList, setServicesList] = useState<CustomerServiceOption[]>([]);
    const [getSubscriptionListState, { get: getSubscriptions }] = useFetch();
    const [getServiceListState, { get: getServices }] = useFetch();
    const [simReplacePending, setSimReplacePending] = useState<boolean>(false);
    const [getReplacePendingState, { get: getReplacePending }] = useFetch();

    const getSimReplacePending = () => {
        if (selectedService)
            getReplacePending<boolean>(getSimReplacePendingRoute(selectedService.id)).then((response: boolean) => {
                setSimReplacePending(response);
            });
    };

    const getPhoneNumber = (phoneNumbersOptions: CustomerServiceOption[]) => (
        prevSelectedService: ICustomService | undefined
    ) => {
        const phoneNumber = phoneNumbersOptions.length ? phoneNumbersOptions[0].value : undefined;
        const noDefaultValue = phoneNumbersOptions.length > 1 ? undefined : phoneNumber;
        const defaultValue = prevSelectedService ?? phoneNumber;

        return hasDefault ? defaultValue : noDefaultValue;
    };

    const getSubscriptionList = () => {
        const promiseSubscriptionList = getSubscriptions<ISubscriptionPage>(GET_SUBSCRIPTION_LIST_ROUTE);
        const promiseServiceList = getServices<IServicePage>(GET_SERVICE_LIST_ROUTE);

        Promise.all([promiseSubscriptionList, promiseServiceList]).then(
            ([subscriptionListResponse, serviceListResponse]) => {
                const phoneNumbersOptions: CustomerServiceOption[] = [];
                if (subscriptionListResponse?.content.length && serviceListResponse?.content.length) {
                    const activeSubscriptionList = subscriptionListResponse.content.filter(
                        (sub) => sub.status === ESubscriptionStatus.ACTIVE
                    );
                    serviceListResponse.content
                        .filter((service) => service.domain === MOBILE_DOMAIN)
                        .forEach((service) => {
                            const subscription = activeSubscriptionList.find(
                                (sub) => sub.id === service.subscriptionId
                            );
                            if (subscription) {
                                phoneNumbersOptions.push({
                                    label: service.name,
                                    value: {
                                        catalogOfferCode: subscription.catalogOfferCode,
                                        id: service.id,
                                        name: service.name,
                                        status: subscription.status,
                                        subscriptionId: service.subscriptionId,
                                    },
                                });
                            }
                        });

                    setSelectedService(getPhoneNumber(phoneNumbersOptions));
                }
                setServicesList(phoneNumbersOptions);
                if (setShowNoServiceChildren) {
                    setShowNoServiceChildren(phoneNumbersOptions.length ? false : true);
                }
            }
        );
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(getSubscriptionList, []);

    const handleChangeSelectService = (selectedPhoneNumber: number) => {
        const serviceSelected = servicesList.find((service) => service.value.id === selectedPhoneNumber);
        if (serviceSelected) {
            setSelectedService(serviceSelected.value);
        }
    };

    return {
        getSubscriptionList,
        getSubscriptionListState,
        getServiceListState,
        handleChangeSelectService,
        selectedService,
        servicesList,
        simReplacePending,
        getReplacePendingState,
        getSimReplacePending,
    };
};
